import { doc, getDoc } from 'firebase/firestore';
import { customerService } from './customer-service';
import { jobService } from './job-service';
import { getJobOrders } from './job-order-service';
import { db, storage } from './firebase-init';
import { ref, listAll, getDownloadURL } from 'firebase/storage';

/**
 * SashService — read-only aggregation layer for the Sash floating widget.
 *
 * Does NOT start any Firebase listeners. jobService and customerService listeners
 * are already running (started by JobContext and CustomerContext on auth). This
 * service reads from their in-memory snapshots + makes targeted async calls.
 */

const norm = (s) => (s || '').trim().toLowerCase();

// pdfUrls map keys, as written by src/services/pdf-storage.js (savePDFUrlToJob)
// onto both the jobs/{id} and cutlistJobs/{id} documents. Order = display order.
const DOC_TYPES = [
  { key: 'estimate',   label: 'Estimate' },
  { key: 'cutlist',    label: 'Cut List' },
  { key: 'glassorder', label: 'Glass' },
  { key: 'flexscreen', label: 'FlexScrn' },
  { key: 'shoporder',  label: 'Shop' },
  { key: 'joborder',   label: 'Job Order' },
];
const DOC_LABEL = Object.fromEntries(DOC_TYPES.map((d) => [d.key, d.label]));
const DOC_ORDER = Object.fromEntries(DOC_TYPES.map((d, i) => [d.key, i]));

const prettyKey = (key) =>
  String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .trim();

/**
 * Format a raw job / job-order status string for display.
 * e.g. "awaiting_production" → "Awaiting Production". No status-set mapping —
 * jobs and job orders use different sets and we show whatever is on the doc.
 */
export function prettyStatus(status) {
  if (!status) return '';
  return prettyKey(status);
}

/**
 * Collect the generated documents for a job / contract, labelled by type.
 *
 * Primary source is the `pdfUrls` map that pdf-storage.js writes onto the job
 * and cutlistJobs documents (same map the Jobs Database page reads). Falls back
 * to listing Firebase Storage at jobDocuments/{year}/{contractNumber} when no
 * map is present (older jobs, or the job row didn't carry it).
 *
 * @returns {Promise<Array<{ key: string, label: string, url: string }>>}
 */
async function getJobDocuments({ job, contractNumber }) {
  let map = { ...(job?.pdfUrls || {}) };

  if (job?.cutlistJobId) {
    try {
      const snap = await getDoc(doc(db, 'cutlistJobs', job.cutlistJobId));
      if (snap.exists()) {
        map = { ...(snap.data().pdfUrls || {}), ...map };
      }
    } catch {
      /* ignore — fall back to what we have */
    }
  }

  let docs = Object.entries(map)
    .filter(([, url]) => typeof url === 'string' && url)
    .map(([key, url]) => ({ key, label: DOC_LABEL[key] || prettyKey(key), url }));

  // Fallback: no map on the doc(s) — list Storage directly.
  if (docs.length === 0 && contractNumber) {
    const folder = String(contractNumber).trim().replace(/\s+/g, '_');
    const thisYear = new Date().getFullYear();
    for (const year of [thisYear, thisYear - 1, thisYear - 2]) {
      try {
        const { items } = await listAll(
          ref(storage, `jobDocuments/${year}/${folder}`)
        );
        if (items.length === 0) continue;
        docs = await Promise.all(
          items.map(async (item) => {
            const key = item.name.replace(/\.pdf$/i, '');
            return {
              key,
              label: DOC_LABEL[key] || prettyKey(key),
              url: await getDownloadURL(item),
            };
          })
        );
        break;
      } catch {
        /* try next year */
      }
    }
  }

  return docs.sort(
    (a, b) => (DOC_ORDER[a.key] ?? 99) - (DOC_ORDER[b.key] ?? 99)
  );
}

/**
 * Find a person by name and return each match with their jobs / job orders and
 * PDF download URLs.
 *
 * Most job customers do NOT have a record in the standalone `customers`
 * collection — jobs and job orders carry the customer's name/phone/address
 * inline on the document. So the primary lookup is against jobService.getJobs()
 * (job.customerName) and getJobOrders() (order.customer.name), with
 * customerService.searchCustomers() merged in as an additional source.
 *
 * @param {string} searchTerm
 * @returns {Promise<Array<{
 *   customer: { id?: string, name: string, phone?: string, email?: string, address?: string },
 *   jobOrders: Array<{
 *     order: { id: string, contractNumber?: string, status?: string, statusLabel: string },
 *     documents: Array<{ key: string, label: string, url: string }>
 *   }>
 * }>>}
 */
export async function findPerson(searchTerm) {
  const term = norm(searchTerm);
  if (term.length < 2) return [];

  const [crmCustomers, allOrders] = await Promise.all([
    customerService.searchCustomers(searchTerm),
    getJobOrders(),
  ]);
  const allJobs = jobService.getJobs();

  const matchingJobs = allJobs.filter((j) => norm(j.customerName).includes(term));
  const matchingOrders = allOrders.filter((o) => norm(o.customer?.name).includes(term));

  // Merge every source into one person-per-name map. Name (normalised) is the
  // join key since inline job/order customers have no shared Firestore id.
  const people = new Map();
  const getPerson = (name) => {
    const key = norm(name);
    if (!people.has(key)) {
      people.set(key, {
        customer: { name: (name || '').trim() },
        jobs: [],
        orders: [],
      });
    }
    return people.get(key);
  };
  // Fill in contact fields we don't already have, preferring the first non-empty.
  const enrich = (customer, { id, phone, email, address } = {}) => {
    if (id && !customer.id) customer.id = id;
    if (phone && !customer.phone) customer.phone = phone;
    if (email && !customer.email) customer.email = email;
    if (address && !customer.address) customer.address = address;
  };

  for (const c of crmCustomers) {
    const p = getPerson(c.name);
    enrich(p.customer, {
      id: c.id,
      phone: c.phone || c.phoneNumber,
      email: c.email,
      address: c.address,
    });
  }

  for (const job of matchingJobs) {
    const p = getPerson(job.customerName);
    enrich(p.customer, {
      phone: job.phoneNumber,
      email: job.customerEmail,
      address: job.address,
    });
    p.jobs.push(job);
  }

  for (const order of matchingOrders) {
    const c = order.customer || {};
    const p = getPerson(c.name);
    enrich(p.customer, {
      id: c.id,
      phone: c.phone || c.phoneNumber,
      email: c.email,
      address: c.address || c.locationLabel,
    });
    p.orders.push(order);
  }

  return Promise.all(
    Array.from(people.values()).map(async ({ customer, jobs, orders }) => {
      // Dedupe by contract number so a job and its job order collapse into one
      // row. The calendar job wins (authoritative production status + the
      // pdfUrls map), then standalone job orders, then any job order linked
      // only by customer.id.
      const linkedOrders = allOrders.filter(
        (o) => o.customer?.id && customer.id && o.customer.id === customer.id
      );
      const seen = new Set();
      const rows = [];
      for (const { src, isJob } of [
        ...jobs.map((src) => ({ src, isJob: true })),
        ...orders.map((src) => ({ src, isJob: false })),
        ...linkedOrders.map((src) => ({ src, isJob: false })),
      ]) {
        const key = src.contractNumber
          ? `c:${String(src.contractNumber).trim().toLowerCase()}`
          : `id:${src.id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        rows.push({ src, isJob });
      }

      const jobOrders = await Promise.all(
        rows.map(async ({ src, isJob }) => {
          // For a job order row, borrow a same-contract calendar job (if any)
          // so we can still resolve its pdfUrls map / cutlistJobId.
          const job = isJob
            ? src
            : allJobs.find(
                (j) =>
                  j.contractNumber &&
                  src.contractNumber &&
                  norm(j.contractNumber) === norm(src.contractNumber)
              ) || null;

          const documents = await getJobDocuments({
            job,
            contractNumber: src.contractNumber,
          });

          return {
            order: {
              id: src.id,
              contractNumber: src.contractNumber,
              status: src.status,
              statusLabel: prettyStatus(src.status),
            },
            documents,
          };
        })
      );

      return { customer, jobOrders };
    })
  );
}

/**
 * Return a daily briefing using jobService's in-memory snapshot (no extra
 * Firebase calls). Callers must ensure jobService has started its listener
 * (JobContext does this on auth) before calling this.
 *
 * @returns {{
 *   soldOutThrough: Date | null,
 *   scheduledThrough: Date | null,
 *   todayJobCount: number,
 *   todayJobs: object[],
 *   tomorrowJobCount: number,
 *   tomorrowJobs: object[],
 * }}
 */
export function getDailyBriefing() {
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  const todayJobs = jobService.getJobsForDate(today);
  const tomorrowJobs = jobService.getJobsForDate(tomorrow);
  const soldOutThrough = jobService.getSoldOutThroughDate();
  const scheduledThrough = jobService.getFurthestScheduledDate();

  return {
    soldOutThrough,
    scheduledThrough,
    todayJobCount: todayJobs.length,
    todayJobs,
    tomorrowJobCount: tomorrowJobs.length,
    tomorrowJobs,
  };
}
